// @flow

import React from 'react';
import { Translate } from 'react-redux-i18n';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { getCurrentPcId, getCurrentShoppingCenter } from '../../../utils/urlUtils';
import { formatThousand } from '../../../utils/formatUtils';
import { timestampToISO } from '../../../utils/dateUtils';
import type { BranchLevels, MallStore, ShoppingCenter } from '../../../types/shoppingCenterType';
import shoppingCenterApi from '../../../network/api/shoppingCenterApi';
import ExpendableTab from '../../../components/ExpendableTab';
import SurfaceConvertor from '../../common/SurfaceConvertor';
import CurrencyConvertor from '../../common/CurrencyConvertor';
import { ANCHORS_MIN_GLA } from '../../../const';

type Props = {
  branchLevels: BranchLevels[],
  location: Location,
  baseShoppingCenterList: ShoppingCenter[]
}

type State = {
  stores: MallStore[],
  selectedBranches: number[],
  selectedStore: number[],
  selectedBranch: number,
  filterAnchor: boolean,
  branchExtended: boolean,
}

class Store extends React.Component<Props, State> {
  state: State = {
    stores: [],
    selectedBranches: [],
    selectedStore: [],
    selectedBranch: 0,
    filterAnchor: false,
    branchExtended: true,
  };

  componentDidMount() {
    shoppingCenterApi.fetchStores(getCurrentPcId())
      .then(resp => resp.json())
      .then(json => this.setState({ stores: json }));
  }

  updateSelectedBranch = (branchId: number) => {
    if (this.state.selectedBranches.includes(branchId)) {
      this.setState({
        selectedBranches: this.state.selectedBranches.filter(id => id !== branchId),
      });
    } else {
      const { selectedBranches } = this.state;
      selectedBranches.push(branchId);
      this.setState({
        selectedBranches,
      });
    }
  };

  updateExpandedBranch = (branchId: number) => {
    this.setState({
      selectedBranch: this.state.selectedBranch === branchId ? 0 : branchId,
    });
  };

  updateSelectedStore = (storeId: number) => {
    if (this.state.selectedStore.includes(storeId)) {
      this.setState({ selectedStore: this.state.selectedStore.filter(id => id !== storeId) });
    } else {
      const stores = this.state.selectedStore;
      stores.push(storeId);
      this.setState({ selectedStore: stores });
    }
  };

  getBranchLabel = (parentBranchId: number, branchId: number) => {
    const branch = this.props.branchLevels
      .filter(branchLevel => branchLevel.branchLevel1Id === parentBranchId)[0];
    if (!branch) {
      return null;
    }
    const subBranch = branch.subBranches
      .filter(branchLevel => branchLevel.branchLevel2Id === branchId)[0];
    if (!subBranch) {
      return null;
    }
    return `${subBranch.branchLevel1Label}/${subBranch.branchLevel2Label}`;
  };

  render() {
    const shoppingCenter =
      getCurrentShoppingCenter(this.props.location.search, this.props.baseShoppingCenterList);
    const totalGla = this.props.branchLevels.reduce((acc: number, curr: BranchLevels) => acc + curr.suGlaInSquareMeters, 0);
    return (
      <div className="shopping-center-store">
        <div className="title">
          <Translate value="mallData.STORE_LABEL" />
        </div>
        <div className="shopping-center-store-container">
          <div className={`branch-container ${this.state.branchExtended ? 'extended' : ''}`}>
            <div className="section-title">
              <Translate value="mallData.FACT_FIGURE.BRANCH_LEVELS" />
              <div className="section-navigation" onClick={() => this.setState({ branchExtended: !this.state.branchExtended })}>
                <Translate value="mallData.FACT_FIGURE.STORES" />
                <i
                  className="fas fa-sm fa-chevron-right"
                />
              </div>
            </div>
            <div className="branches">
              <div className="branch-level-container">
                <div
                  className={`branch-level-actions ${this.state.filterAnchor ? 'selected' : ''}`}
                >
                  <i
                    role="button"
                    className={`fas fa-filter filter${this.state.filterAnchor ? 'selected' : ''}`}
                    onClick={() => this.setState({ filterAnchor: !this.state.filterAnchor })}
                  />
                  <button
                    className="branch-level-button"
                  >
                    <div className="branch-level-label">
                      <Translate
                        value="mallData.STORE.ANCHOR"
                        anchorNumber={this.state.stores
                          .filter(store => store.glaInSquareMeters > ANCHORS_MIN_GLA).length}
                      />
                    </div>
                    <div className="branch-level-label small label-header">
                      <Translate value="mallData.STORE.SECTOR_SALES" />
                    </div>
                    <div className="branch-level-label small label-header">
                      <Translate value="mallData.STORE.SECTOR_SPREAD" />
                    </div>
                    <div className="branch-level-label tiny" />
                  </button>
                </div>
              </div>
              {
                this.props.branchLevels
                  .sort((a, b) =>
                    b.salesR12mBySquareMeter
                    - a.salesR12mBySquareMeter)
                  .map(branchLevel => (
                    <div
                      key={branchLevel.branchLevel1Label}
                      className="branch-level-container"
                    >
                      <div
                        className={`branch-level-actions ${this.state.selectedBranches.includes(branchLevel.branchLevel1Id) ? 'selected' : ''}`}
                      >
                        <i
                          role="button"
                          className={`fas fa-filter filter ${this.state.selectedBranches.includes(branchLevel.branchLevel1Id) ? 'selected' : ''}`}
                          onClick={() => this.updateSelectedBranch(branchLevel.branchLevel1Id)}
                        />
                        <button
                          className={`branch-level-button ${this.state.selectedBranches.includes(branchLevel.branchLevel1Id) ? ' selected' : ''}`}
                          onClick={() => this.updateExpandedBranch(branchLevel.branchLevel1Id)}
                        >
                          <div className="branch-level-label">
                            {branchLevel.branchLevel1Label} (
                            {this.state.stores
                              .filter(store => store.parentBranchId === branchLevel.branchLevel1Id
                                && (!this.state.filterAnchor || store.glaInSquareMeters > ANCHORS_MIN_GLA)).length})
                          </div>
                          <div className="branch-level-label small">
                            {
                              branchLevel.salesR12mBySquareMeter ?
                                <CurrencyConvertor
                                  value={formatThousand(parseInt(branchLevel.salesR12mBySquareMeter, 10))}
                                /> :
                                <span>0</span>
                            }

                          </div>
                          <div className="branch-level-label small">
                            {shoppingCenter ? (Math.round((branchLevel.suGlaInSquareMeters / totalGla) * 10000) / 100).toFixed(1) : ''}%
                          </div>
                          <div className="branch-level-label tiny">
                            <i
                              className={`fas fa-chevron-down icon ${this.state.selectedBranch === branchLevel.branchLevel1Id ? 'expended' : ''}`}
                            />
                          </div>
                        </button>
                      </div>
                      <ExpendableTab
                        isExpended={this.state.selectedBranch === branchLevel.branchLevel1Id}
                      >
                        {
                          branchLevel.subBranches
                            .sort((a, b) => b.salesR12mBySquareMeter - a.salesR12mBySquareMeter)
                            .filter(subBranch => subBranch.salesR12mBySquareMeter > 0 || subBranch.suGlaInSquareMeters > 0)
                            .map(subBranch => (
                              <div
                                key={subBranch.branchLevel2Label}
                                className="sub-branch"
                              >
                                <div className="branch-level-label">
                                  {subBranch.branchLevel2Label}
                                </div>
                                <div className="branch-level-label small">
                                  {subBranch.salesR12mBySquareMeter ?
                                    <CurrencyConvertor
                                      value={formatThousand(parseInt(subBranch.salesR12mBySquareMeter, 10))}
                                    /> :
                                    <span>0</span>
                                  }
                                </div>
                                <div className="branch-level-label tiny">
                                  {shoppingCenter ? (Math.round((subBranch.suGlaInSquareMeters / totalGla) * 10000) / 100).toFixed(1) : ''}%
                                </div>
                              </div>
                            ))
                        }
                      </ExpendableTab>
                    </div>))
              }
            </div>
            <div className="branch-level-bottom-legend">
              <div>
                <Translate value="mallData.STORE.SECTOR_SALES_LEGEND" />
              </div>
              <div>
                <Translate value="mallData.STORE.SECTOR_SPREAD_LEGEND" />
              </div>
            </div>
          </div>
          <div className={`store-container ${!this.state.branchExtended ? 'extended' : ''}`}>
            <div className="section-title">
              <div className="section-navigation" onClick={() => this.setState({ branchExtended: !this.state.branchExtended })}>
                <i
                  className="fas fa-sm fa-chevron-left"
                />
                <Translate value="mallData.FACT_FIGURE.BRANCH_LEVELS" />
              </div>
              <Translate value="mallData.FACT_FIGURE.STORES" />
            </div>
            <div className="stores">
              {
                this.props.branchLevels &&
                this.state.stores.filter((store) => {
                  if (this.state.selectedBranches.length === 0) {
                    return !this.state.filterAnchor || store.glaInSquareMeters > ANCHORS_MIN_GLA;
                  }
                  return this.state.selectedBranches.includes(store.parentBranchId)
                    && (!this.state.filterAnchor || store.glaInSquareMeters > ANCHORS_MIN_GLA);
                }).map((store, index) => (
                  <div
                    className="store-data-container"
                    key={`${store.storeId}-${store.retailerId}-${index.toString()}`}
                  >
                    <button
                      onClick={() => this.updateSelectedStore(store.storeId)}
                      className="store"
                    >
                      {store.storeName}
                      <i
                        className={`fal fa-chevron-down icon ${this.state.selectedStore.includes(store.storeId) ? 'expended' : ''}`}
                      />
                    </button>
                    <ExpendableTab
                      isExpended={this.state.selectedStore.includes(store.storeId)}
                    >
                      <div className="store-data">
                        <Translate
                          className="store-data-label"
                          value="mallData.STORE.BRANCH_LEVEL"
                        />
                        <span>
                          {this.getBranchLabel(store.parentBranchId, store.branchId)}
                        </span>
                      </div>
                      <div className="store-data">
                        <Translate
                          className="store-data-label"
                          value="mallData.STORE.GLA"
                        />
                        <SurfaceConvertor
                          className="store-data-value"
                          value={store.glaInSquareMeters}
                        />
                      </div>
                      <div className="store-data">
                        <Translate
                          className="store-data-label"
                          value="mallData.STORE.OPENING_DATE"
                        />
                        <span>
                          {timestampToISO(store.openingDate)}
                        </span>
                      </div>
                    </ExpendableTab>
                  </div>
                ))
              }
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default withRouter(connect(state => ({
  shoppingCenterMedia: state.shoppingCenterFilters.shoppingCenterMedia,
  baseShoppingCenterList: state.shoppingCenterFilters.baseShoppingCenterList,
  branchLevels: state.shoppingCenterFilters.branchLevelList,
}))(Store));
